import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, DollarSign, GraduationCap, Radar } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { API, trackEvent, formatApiError, QUIZ_URL } from "@/lib/api";

const PERKS = [
  { icon: GraduationCap, t: "Free coach membership", d: "Full access to the Interpretive Guide, the P.A.R. practice system, and every training asset — on us.", id: "coach-perk-free" },
  { icon: Radar, t: "See your students' wiring", d: "When your students take the Wired2Win assessment, you learn how they take instruction, what conditions they need, and how they respond under pressure.", id: "coach-perk-insight" },
  { icon: DollarSign, t: "$10 per annual signup", d: "Share your personal link. Every annual membership that comes through it is tallied automatically and paid monthly.", id: "coach-perk-commission" },
];

export default function Coaches() {
  const [form, setForm] = useState({ name: "", email: "", where: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Certified Coach Program — Wired 2 Win Golf";
    trackEvent("page_view", { page: "coaches" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/coach-inquiries`, form);
      setStatus("done");
      trackEvent("coach_inquiry_submitted");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="coaches-page" className="pt-32 lg:pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <Overline className="mb-6"><MaskedLine>For golf coaches</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase max-w-4xl">
          <MaskedLine delay={0.15}>Coach the player</MaskedLine>
          <MaskedLine delay={0.3}>in front of you —</MaskedLine>
          <MaskedLine delay={0.45}><span className="text-volt">not a generic model.</span></MaskedLine>
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed">
            Wired2Win isn't another swing methodology. It's a launch monitor for the brain — a precise read on how a golfer is wired, so your instruction actually sticks. Become a certified Wired2Win coach.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PERKS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <motion.div data-testid={p.id} whileHover={{ y: -6 }} transition={{ duration: 0.25 }}
                className="border border-white/10 bg-ink p-8 h-full hover:border-volt/60 transition-colors duration-300">
                <p.icon className="w-7 h-7 text-volt mb-6" />
                <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{p.t}</h2>
                <p className="text-white/60 text-sm leading-relaxed">{p.d}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <a href={QUIZ_URL} data-testid="coaches-quiz-cta"
                onClick={() => trackEvent("cta_click", { location: "coaches" })}
                className="group inline-flex items-center gap-2 border border-volt text-volt font-display font-bold px-7 py-4 hover:bg-volt hover:text-obsidian transition-colors duration-200">
                Take the Assessment Yourself First
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
              </a>
            </motion.span>
            <p className="text-white/40 text-xs max-w-xs">Five minutes. You'll immediately see how it changes a coaching conversation.</p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-white/10 bg-ink/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Overline>Apply</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4">
              Tell us where <span className="text-volt">you teach.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base leading-relaxed max-w-md">
              Dave reviews every inquiry personally. If the program fits, you'll get a welcome email with your free coach membership and your personal referral link.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            {status === "done" ? (
              <div data-testid="coaches-success" className="border border-volt/40 bg-volt/5 p-10 flex flex-col items-start gap-4">
                <span className="w-12 h-12 bg-volt flex items-center justify-center"><Check className="w-6 h-6 text-obsidian" /></span>
                <h3 className="font-display font-bold text-3xl tracking-tight">Inquiry received.</h3>
                <p className="text-white/60 text-sm leading-relaxed">Dave will be in touch personally — usually within a couple of days. Meanwhile, take the assessment above so you've felt it from the player's side.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 bg-ink p-8 sm:p-10 space-y-5" data-testid="coaches-inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="coach-name" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Name</label>
                    <input id="coach-name" required value={form.name} onChange={set("name")} data-testid="coaches-name-input"
                      className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="coach-email" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Email</label>
                    <input id="coach-email" type="email" required value={form.email} onChange={set("email")} data-testid="coaches-email-input"
                      className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <label htmlFor="coach-where" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Where do you coach?</label>
                  <input id="coach-where" value={form.where} onChange={set("where")} data-testid="coaches-where-input"
                    className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="Club, academy, or facility" />
                </div>
                <div>
                  <label htmlFor="coach-message" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Anything else? (optional)</label>
                  <textarea id="coach-message" rows={4} value={form.message} onChange={set("message")} data-testid="coaches-message-input"
                    className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors resize-none" placeholder="Your students, your program, your questions..." />
                </div>
                {error && <p data-testid="coaches-error" className="text-red-400 text-xs">{error}</p>}
                <button type="submit" disabled={status === "loading"} data-testid="coaches-submit-button"
                  className="w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50">
                  {status === "loading" ? "Sending..." : "Apply to the Coach Program"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
